import * as React from 'react';
// Import form-input hooks.
import {
	useInputPerilDeductible,
	UseInputPerilDeductibleResults,
} from '@app/hooks/view/input';
// Import mui modules.
import {
	EditOutlined,
	MonetizationOnOutlined,
} from '@mui/icons-material';
// Import props.
import {
	ComparativePerilsDeductiblesSummaryProps,
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
    IconButtonProps,
} from '@app/props';
// Import enumerations.
import {
    AppThemeColor,
	EntityInstanceType,
} from '@app/enumerations';
// Import api models.
import {
    ComparisonSummaryPerilDeductible,
	PerilDeductible,
} from '@app/models/api';

interface UseComparativePerilsDeductiblesSummaryParameters {
	props: ComparativePerilsDeductiblesSummaryProps;
}

interface UseComparativePerilsDeductiblesSummaryResults {
	oldPerilsDeductiblesInputs: UseInputPerilDeductibleResults;
	newPerilsDeductiblesInputs: UseInputPerilDeductibleResults;
	baseId:string;
	applyDeductiblesComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPerilsDeductiblesComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyPerilsDeductiblesErrorStatusComparativeDataHeaderProps: () => ComparativeDataHeaderProps | undefined;
	applyPerilsDeductiblesComparativeDataRowProps: () => ComparativeDataRowProps[];
}

const useComparativePerilsDeductiblesSummary = (params: UseComparativePerilsDeductiblesSummaryParameters): UseComparativePerilsDeductiblesSummaryResults => {

	// #region exported methods

	/**
	 * Returns deductibles comparative data header props. 
	 **/
	const applyDeductiblesComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${baseId}-deductibles-header`,
			isSticky: true,
			title: 'Deductibles',
			titleFontSize: 'md',
			titleIcon: MonetizationOnOutlined,
		};
	};

	/**
	 * Returns perils deductibles comparative data header props. 
	 **/
	const applyPerilsDeductiblesComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${baseId}-perilsDeductibles-header`,
			isSticky: true,
			title: 'Peril deductibles',
			titleFontSize: 'sm',
			iconButtons: getHeaderIconButtons(),
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	/**
	 * Returns error status comparative data header props. 
	 **/
	const applyPerilsDeductiblesErrorStatusComparativeDataHeaderProps = (): ComparativeDataHeaderProps | undefined => {
		let buildingComparativeErrorStatusDataHeaderProps: ComparativeDataHeaderProps | undefined = undefined;
		
		if (params.props.comparisonPerilsDeductibleSummary.requiresReview
				&& params.props.comparisonPerilsDeductibleSummary.allowEdits) {
			buildingComparativeErrorStatusDataHeaderProps = {
				id: `${baseId}-status-header`,
				isSticky: false,
				title: 'Incomplete - Additional Information Required',
				titleColor: AppThemeColor.ERROR,
				titleFontSize: 'xxsm',
				titleFontStyle: 'italic',
				titleFontWeight: 'normal',
			};
		}

		return buildingComparativeErrorStatusDataHeaderProps;
	};

	/**
	 * Returns perils deductibles comparative data row props. 
	 **/
	const applyPerilsDeductiblesComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return params.props.comparisonPerilsDeductibleSummary.comparisonPerilDeductibleSummaries.map((comparisonPerilDeductibleSummary: ComparisonSummaryPerilDeductible) => {
			return {
				fieldLabel: newPerilsDeductiblesInputs.getPerilDeductiblesLabelByPerilCode(comparisonPerilDeductibleSummary.perilCode),
				oldValue: comparisonPerilDeductibleSummary.oldSelectedPerilDeductible?.deductibleCode ?? undefined,
				newValue: comparisonPerilDeductibleSummary.newSelectedPerilDeductible?.deductibleCode ?? undefined,
				hideNewValueIfSame: true,
			};
		});
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches the header icons.
	 */
	const getHeaderIconButtons = (): IconButtonProps[] => {
		let iconButtons: IconButtonProps[] = [];
		if (params.props.comparisonPerilsDeductibleSummary.allowEdits) {
			iconButtons.push({
				label: 'Edit',
				onClick: () => params.props.onEditPerilsDeductibles(),
				icon: EditOutlined,
				ref: editButtonRef,
			});
		}

		return iconButtons;
	}

	/**
	 * Generates id for the perils deductibles inputs.
	 */
	const generateIdForPerilsDeductiblesInputs = (instanceType: EntityInstanceType): string => {
		return `${baseId}-${instanceType}-perilsDeductibles`;
	}

	const baseId: string = React.useMemo(
		() => params.props.id,
		[params.props.id],
	);

	const getOldSelectedPerilsDeductibles = (): PerilDeductible[] => {
		return params.props.comparisonPerilsDeductibleSummary.comparisonPerilDeductibleSummaries.map((comparisonPerilDeductibleSummary: ComparisonSummaryPerilDeductible) => {
			return comparisonPerilDeductibleSummary.oldSelectedPerilDeductible;
		}).filter<PerilDeductible>((oldSelectedPerilDeductible: PerilDeductible | undefined): oldSelectedPerilDeductible is PerilDeductible => {
			return oldSelectedPerilDeductible !== undefined;
		});
	}

	const getNewSelectedPerilsDeductibles = (): PerilDeductible[] => {
		return params.props.comparisonPerilsDeductibleSummary.comparisonPerilDeductibleSummaries.map((comparisonPerilDeductibleSummary: ComparisonSummaryPerilDeductible) => {
			return comparisonPerilDeductibleSummary.newSelectedPerilDeductible;
		}).filter<PerilDeductible>((newSelectedPerilDeductible: PerilDeductible | undefined): newSelectedPerilDeductible is PerilDeductible => {
			return newSelectedPerilDeductible !== undefined;
		});
	}
	// #endregion private methods


	// #region variables

	const oldSelectedPerilsDeductibles: PerilDeductible[] = React.useMemo(
		() => getOldSelectedPerilsDeductibles(),
		[params.props.comparisonPerilsDeductibleSummary.comparisonPerilDeductibleSummaries]
	);

	const newSelectedPerilsDeductibles: PerilDeductible[] = React.useMemo(
		() => getNewSelectedPerilsDeductibles(),
		[params.props.comparisonPerilsDeductibleSummary.comparisonPerilDeductibleSummaries]
	);

	// Old perils deductibles inputs.
	const oldPerilsDeductiblesInputs: UseInputPerilDeductibleResults = useInputPerilDeductible({
		id: generateIdForPerilsDeductiblesInputs(EntityInstanceType.OLD),
		disabled: false,
		required: true,
		possiblePerils: params.props.possiblePerils,
		selectedPerilDeductibles: oldSelectedPerilsDeductibles,
	});

	// New perils deductibles inputs
	const newPerilsDeductiblesInputs: UseInputPerilDeductibleResults = useInputPerilDeductible({
		id: generateIdForPerilsDeductiblesInputs(EntityInstanceType.NEW),
		disabled: false,
		required: true,
		possiblePerils: params.props.possiblePerils,
		selectedPerilDeductibles: newSelectedPerilsDeductibles,
	});

	const focusToErrorOnMount: boolean = React.useMemo(() => params.props.focusToErrorOnMount ?? false, [params.props.focusToErrorOnMount]);
	const editButtonRef: React.RefObject<HTMLButtonElement> = React.useRef<HTMLButtonElement>(null);

	// #endregion private variables


	// #region life-cycle methods.

	React.useEffect(() => {
		if (focusToErrorOnMount
			&& editButtonRef.current !== null) {
			editButtonRef.current.focus();
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		oldPerilsDeductiblesInputs: oldPerilsDeductiblesInputs,
		newPerilsDeductiblesInputs: newPerilsDeductiblesInputs,
		baseId: baseId,
		applyDeductiblesComparativeDataHeaderProps: applyDeductiblesComparativeDataHeaderProps,
		applyPerilsDeductiblesComparativeDataHeaderProps: applyPerilsDeductiblesComparativeDataHeaderProps,
		applyPerilsDeductiblesErrorStatusComparativeDataHeaderProps: applyPerilsDeductiblesErrorStatusComparativeDataHeaderProps,
		applyPerilsDeductiblesComparativeDataRowProps: applyPerilsDeductiblesComparativeDataRowProps,
	};
}

// Export parameters.
export {
	UseComparativePerilsDeductiblesSummaryParameters,
}

// Export results.
export {
	UseComparativePerilsDeductiblesSummaryResults,
}

export default useComparativePerilsDeductiblesSummary;